import Main from 'app/frontend/base/components/Main'
import Select from 'app/frontend/base/components/Select'
import { Button } from '@/components/ui/button'
import {
  DevEncounter,
  DevInstance,
  getDevEncounterCreatureSaved,
  getDevEncounterCreatures,
  syncDevEncounterCreatures,
} from 'app/frontend/base/api/devEncounterCreatures'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useMemo, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { toast } from 'sonner'
import { Loader2 } from 'lucide-react'
import CreatureCircleEditor from './CreatureCircleEditor'
import { saveMissingDefaultCreatureImages } from './creatureImageExport'

const EncounterCreaturesPage = () => {
  const queryClient = useQueryClient()
  const [searchParams, setSearchParams] = useSearchParams()
  const instanceId = searchParams.get('instance') ?? undefined
  const encounterId = searchParams.get('encounter') ?? undefined
  const [syncing, setSyncing] = useState(false)
  const [syncStatus, setSyncStatus] = useState<string>()

  const { data, isLoading, error } = useQuery({
    queryKey: ['devEncounterCreatures'],
    queryFn: getDevEncounterCreatures,
  })

  const instances = data?.instances ?? []

  const selectedInstance = useMemo(
    () => instances.find((instance) => String(instance.id) === instanceId),
    [instances, instanceId],
  )

  const encounters = selectedInstance?.encounters ?? []

  const selectedEncounter = useMemo(
    () => encounters.find((encounter) => String(encounter.id) === encounterId),
    [encounters, encounterId],
  )

  const { data: savedData, refetch: refetchSaved } = useQuery({
    queryKey: ['devEncounterCreatureSaved', encounterId],
    queryFn: () => getDevEncounterCreatureSaved(Number(encounterId)),
    enabled: !!encounterId,
  })

  const savedIds = useMemo(
    () => new Set(savedData?.creatureIds ?? []),
    [savedData],
  )

  const instanceOptions = instances.map((instance: DevInstance) => ({
    value: String(instance.id),
    label: `${instance.name} (${instance.gameVersion})`,
    sortLabel: '',
  }))

  const encounterOptions = encounters.map((encounter: DevEncounter) => ({
    value: String(encounter.id),
    label: encounter.name,
    sortLabel: encounter.name,
  }))

  const updateParams = (next: { instance?: string; encounter?: string }) => {
    const params = new URLSearchParams()
    if (next.instance) params.set('instance', next.instance)
    if (next.encounter) params.set('encounter', next.encounter)
    setSearchParams(params, { replace: true })
  }

  const handleSync = async () => {
    if (!selectedInstance) return

    setSyncing(true)
    try {
      setSyncStatus('Syncing creatures from Blizzard…')
      await syncDevEncounterCreatures(selectedInstance.id)

      setSyncStatus('Generating default images…')
      const refreshed = await getDevEncounterCreatures()
      const instance = refreshed.instances.find(
        (entry) => entry.id === selectedInstance.id,
      )
      if (instance) {
        await saveMissingDefaultCreatureImages(instance.encounters)
      }

      window.location.reload()
    } catch (syncError) {
      setSyncing(false)
      setSyncStatus(undefined)
      toast.error(
        syncError instanceof Error
          ? syncError.message
          : 'Failed to sync encounter creatures',
      )
    }
  }

  const updateCreatureDisabled = (creatureId: number, disabled: boolean) => {
    queryClient.setQueryData(
      ['devEncounterCreatures'],
      (current: { instances: DevInstance[] } | undefined) => {
        if (!current) return current

        return {
          instances: current.instances.map((instance) => ({
            ...instance,
            encounters: instance.encounters.map((encounter) => ({
              ...encounter,
              creatures: encounter.creatures.map((entry) =>
                entry.id === creatureId ? { ...entry, disabled } : entry,
              ),
            })),
          })),
        }
      },
    )
  }

  return (
    <Main title='Encounter creatures'>
      <div className='flex flex-col gap-6'>
        <div className='flex flex-wrap items-end gap-4'>
          <div className='flex flex-col gap-2 min-w-[240px]'>
            <div className='text-sm text-muted-foreground'>Instance</div>
            <Select
              value={instanceId}
              setValue={(value) => {
                const instance = instances.find(
                  (entry) => String(entry.id) === value,
                )
                updateParams({
                  instance: value,
                  encounter: instance?.encounters[0]
                    ? String(instance.encounters[0].id)
                    : undefined,
                })
              }}
              options={instanceOptions}
              isSearchable
              placeholder='Select instance'
            />
          </div>
          <div className='flex flex-col gap-2 min-w-[240px]'>
            <div className='text-sm text-muted-foreground'>Encounter</div>
            <Select
              value={encounterId}
              setValue={(value) => {
                updateParams({
                  instance: instanceId,
                  encounter: value,
                })
              }}
              options={encounterOptions}
              disabled={!selectedInstance}
              placeholder='Select encounter'
            />
          </div>
          <Button
            onClick={handleSync}
            disabled={!selectedInstance || syncing}
            loading={syncing}
          >
            Sync
          </Button>
        </div>

        {syncing ? (
          <div className='flex items-center gap-2 text-muted-foreground'>
            <Loader2 className='h-4 w-4 animate-spin' />
            {syncStatus}
          </div>
        ) : null}

        {isLoading ? (
          <div className='text-muted-foreground'>Loading…</div>
        ) : null}

        {error ? (
          <div className='text-destructive'>
            Failed to load encounter creatures. This page is development-only.
          </div>
        ) : null}

        {!syncing && selectedEncounter ? (
          selectedEncounter.creatures.length === 0 ? (
            <div className='text-muted-foreground'>
              No creatures for this encounter. Select the instance and click
              Sync.
            </div>
          ) : (
            <div className='flex flex-wrap gap-4'>
              {selectedEncounter.creatures.map((creature) => (
                <div
                  key={creature.id}
                  className='basis-full md:basis-[calc((100%-1rem)/2)] grow-0 shrink-0'
                >
                  <CreatureCircleEditor
                    encounterId={selectedEncounter.id}
                    creature={creature}
                    saved={savedIds.has(creature.id)}
                    onSaved={() => {
                      void refetchSaved()
                    }}
                    onDisabledChange={(disabled) => {
                      updateCreatureDisabled(creature.id, disabled)
                    }}
                  />
                </div>
              ))}
            </div>
          )
        ) : null}
      </div>
    </Main>
  )
}

export default EncounterCreaturesPage
